import { Button, IconButton, Stack, Typography } from '@mui/material';
import axios, { AxiosError } from 'axios';
import moment from 'moment';
import { useCallback, useState } from 'react';
import {
	BooleanInput,
	Datagrid,
	ExportButton,
	FilterButton,
	FunctionField,
	List,
	ReferenceField,
	ReferenceInput,
	SelectInput,
	TextField,
	TextInput,
	useNotify,
	useRefresh,
} from 'react-admin';
import { CgFileDocument } from 'react-icons/cg';
import { MdDelete, MdEditCalendar } from 'react-icons/md';
import constants from '../../constants';
import { IPrivateSessionAppointment } from '../../types';
import { getLocalizedErrorMessage } from '../../utils/functions';
import CreatePrivateSession from './Dialogs/CreateSessionDialog';
import DeleteSessionDialog from './Dialogs/DeleteSessionDialog';
import EditSessionDialog from './Dialogs/EditSessionDialog';

const createdByTitles: { [key: string]: string } = {
	user: 'משתמש',
	admin: 'אדמין',
	expert: 'מומחה',
};
const cancelledByTitles: { [key: string]: string } = {
	member: 'משתמש',
	admin: 'אדמין',
	host: 'מומחה',
	expireUnpaidIntents: 'תשלום פג תוקף',
};

const serviceTypes: { [key: string]: string } = {
	'1TO1': 'אחד על אחד',

	SAFE_SPACE: 'מרחב בטוח',
	SAFE_SPACE_TRIAL: 'מרחב בטוח - ניסיון',
	HELPING_HAND: 'עזרה ראשונה רגשית',
};
const serviceTypesColors: { [key: string]: string } = {
	'1TO1': 'bg-amber-200',
	SAFE_SPACE: 'bg-purple-200',
	SAFE_SPACE_TRIAL: 'bg-purple-100',
	HELPING_HAND: 'bg-blue-100',
};

const PrivateSessionsList = () => {
	const refresh = useRefresh();

	const notify = useNotify();
	const handleCancelSession = useCallback(
		async (id: string, silent?: boolean) => {
			try {
				if (!id) throw new Error();
				await axios.delete(`${constants.API_URL}member/private_service/appointment/`, {
					data: { slot_id: id, silent },
				});
				notify(`המפגש נמחק (${silent ? 'לא נשלחו מיילים' : 'נשלחו מיילים'})`, { type: 'success' });
				refresh();
			} catch (error) {
				notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
			} finally {
				setSessionToDelete(null);
			}
		},
		[notify, refresh]
	);
	const [sessionToDelete, setSessionToDelete] = useState<IPrivateSessionAppointment | null>(null);
	const [sessionToEdit, setSessionToEdit] = useState<IPrivateSessionAppointment | null>(null);
	const [showCreateSessionDialog, setShowCreateSessionDialog] = useState<boolean>(false);
	// GH-666: admin-only route — not bound by the cancellation window or the booking quotas.
	const handleRescheduleSession = useCallback(
		async (id: string, newTime: string, silent?: boolean) => {
			try {
				if (!id) throw new Error();
				await axios.put(`${constants.API_URL}member/private_service/appointment/admin`, {
					old_slot_id: id,
					new_time: newTime,
					silent,
				});
				notify(`מועד המפגש עודכן (${silent ? 'לא נשלחו התראות' : 'נשלחו התראות'})`, { type: 'success' });
				refresh();
			} catch (error) {
				notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
			} finally {
				setSessionToEdit(null);
			}
		},
		[notify, refresh]
	);
	return (
		<>
			<CreatePrivateSession
				open={showCreateSessionDialog}
				onClose={() => {
					setShowCreateSessionDialog(false);
				}}
			/>
			<DeleteSessionDialog
				open={sessionToDelete !== null}
				handleCancelSession={handleCancelSession}
				onClose={() => {
					setSessionToDelete(null);
				}}
				sessionToDelete={sessionToDelete}
			/>
			<EditSessionDialog
				open={sessionToEdit !== null}
				handleRescheduleSession={handleRescheduleSession}
				onClose={() => {
					setSessionToEdit(null);
				}}
				sessionToEdit={sessionToEdit}
			/>
			<List
				actions={
					<Stack direction="row" spacing={2} style={{ display: 'flex', justifyContent: 'space-between' }}>
						<Button
							onClick={() => {
								setShowCreateSessionDialog(true);
							}}
							variant="text"
						>
							קביעת מפגש
						</Button>
						<FilterButton />
						<ExportButton />
					</Stack>
				}
				sort={{ field: 'time', order: 'DESC' }}
				filterDefaultValues={{ payment_confirm: true, cancelled: false }}
				filters={[
					// GH-686 (Keren): `instructor` is the MEMBER list (active + showInSubscription only) —
					// experts who are active but not in the subscription (safe-space / private only) were
					// missing from the filter although their sessions are listed. `instructor/all` is the
					// admin list; we only drop inactive ones.
					<ReferenceInput
						source="host"
						reference="instructor/all"
						filter={{ active: true }}
						perPage={999}
						sort={{ field: 'name', order: 'ASC' }}
					>
						<SelectInput optionText={'name'} />
					</ReferenceInput>,
					<TextInput source="email" />,
					<BooleanInput label="תשלום מאושר" source="payment_confirm" defaultValue={true} />,
					<BooleanInput label="מבוטל" source="cancelled" defaultValue={false} />,
					...(new Date() >= constants.SUBSIDY_2026_START
						? [<BooleanInput key="subsidized" label="מסובסד" source="subsidized" />]
						: []),
				]}
			>
				<Datagrid bulkActionButtons={false}>
					<FunctionField
						textAlign="right"
						label="תאריך"
						sortBy="time"
						render={(record: any) => (
							<span className="whitespace-pre-line">
								<div>{`${moment(record.time).format('DD.MM.YY')}`}</div>
								<div className="text-xs">{moment(record.time).format('HH:mm')}</div>
							</span>
						)}
					/>
					<TextField textAlign="right" label="מייל" source="email" />
					<FunctionField
						label="שירות"
						render={(record: any) => (
							<div>
								<div className="font-medium line-clamp-1 w-44 truncate text-[0.8rem]">
									{record.service?.title?.he} <span className="text-xs">{record.followup_of ? '(follow up)' : ''}

										{record.type === "SUPPLIER" && " (פגישת ספק)"}
									</span>
								</div>
								<span
									className={`text-[0.6rem] px-1.5 py-0.5 rounded-full ${
										serviceTypesColors[record.type]
									}`}
								>
									{serviceTypes[record.type]}
								</span>
							</div>
						)}
					/>
					<ReferenceField source="host" label="מומחה/ית" reference="instructor/all">
						<TextField textAlign="right" label="מומחה" source="name" />
					</ReferenceField>
					<FunctionField
						textAlign="right"
						label="פגישה בזוג"
						render={(record: any) => {
							if (record.type !== 'SAFE_SPACE') return null;
							if (new Date() < constants.SUBSIDY_2026_START) return null;
							if (!record.pair_number) return null;
							const cycleLabel = record.cycle_start_date
								? `מחזור הטבה מ-${moment(record.cycle_start_date).format('DD/MM/YY')}`
								: undefined;
							return (
								<span
									title={cycleLabel}
									className={`text-xs px-2 py-0.5 rounded-full ${
										record.subsidized ? 'bg-purple-100' : 'bg-orange-100'
									}`}
								>
									{`${record.pair_number}/${constants.SUBSIDY_2026_PAIR_LIMIT}`}
								</span>
							);
						}}
					/>
					<FunctionField
						textAlign="right"
						label="תשלום למטפל"
						render={(record: any) => {
							if (record.type !== 'SAFE_SPACE') return null;
							let payout: number;
							if (record.subsidized) {
								payout =
									record.host_doc?.safe_space_payment ||
									(record.host_doc?.is_psychologist ? 350 : 250);
							} else {
								const basePrice = record.service_price_at_booking ?? record.service?.price ?? 0;
								payout = Math.floor(basePrice * 0.97);
							}
							return `${payout} ₪`;
						}}
					/>
					<FunctionField
						textAlign="right"
						label="תשלום מנוי"
						render={(record: any) => `${record.price ?? 0} ₪`}
					/>

					<FunctionField
						render={(record: any) => (
							<span className="whitespace-pre-line">
								<div>{`${moment(record.createdAt).format('DD.MM.YY')}`}</div>
								<div className="text-xs">{moment(record.createdAt).format('HH:mm')}</div>
							</span>
						)}
						label="נקבע בתאריך"
						source="createdAt"
						showTime
						locales={['he']}
					/>
					<FunctionField
						textAlign="right"
						label='נקבע ע"י'
						render={(record: any) =>
							`${record.createdBy ? createdByTitles[record.createdBy ?? 'user'] : ''}`
						}
					/>
					<FunctionField
						textAlign="right"
						render={(record: any) => (
							<div dir="rtl" className="text-center text-[0.6rem]">
							
								{record.updatedBy ? (
									<div className="whitespace-pre-line">
										{`עודכן ע"י ${createdByTitles[record.updatedBy ?? 'user']}\n`}
										<div>{`(${moment(record.updatedAt).format('DD/MM HH:mm')})`}</div>
									</div>
								) : null}
								{record.cancelled_by ? (
									<div className="whitespace-pre-line text-red-600">{`בוטל ע"י ${
										cancelledByTitles[record.cancelled_by.type ?? 'member']
									}\n`}</div>
								) : null}
							</div>
						)}
					/>
					<FunctionField
						render={(record: IPrivateSessionAppointment) => (
							<IconButton disabled={false} color="primary" onClick={() => setSessionToEdit(record)}>
								<MdEditCalendar />
							</IconButton>
						)}
					/>
					<FunctionField
						render={(record: IPrivateSessionAppointment) => (
							<IconButton disabled={false} color="warning" onClick={() => setSessionToDelete(record)}>
								<MdDelete />
							</IconButton>
						)}
					/>
					<FunctionField
						render={(record: IPrivateSessionAppointment) => (
							<Typography fontSize={10} textAlign={'center'}>
								{record.price && !record.payment_confirm ? 'ממתין לתשלום' : ''}
							</Typography>
						)}
					/>
					<FunctionField
						render={(record: IPrivateSessionAppointment) =>
							!record.price && !record.payment_confirm ? (
								<Button
									onClick={async () => {
										try {
											await axios.post(`${constants.API_URL}member/private_session/confirm/`, {
												appointment_id: record._id,
											});
											notify('המפגש אושר', { type: 'success' });
											refresh();
										} catch (error) {
											notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
										}
									}}
								>
									אישור מפגש
								</Button>
							) : null
						}
					/>
					<FunctionField
						render={(record: IPrivateSessionAppointment) =>
							record?.payment?.sale_invoice_url ? (
								<a target="_blank" rel="noopener noreferrer" href={record.payment.sale_invoice_url}>
									<CgFileDocument color="gray" />
								</a>
							) : null
						}
					/>
				</Datagrid>
			</List>
		</>
	);
};

export default PrivateSessionsList;
